// Mejora fichas (21-oct-2026) — «Posibles duplicados por nombre»: pares del mismo barbero con nombre parecido y
// contactos distintos. Uno por uno: «Fusionar» (une teléfono y correo, re-apunta todo) o «Son personas distintas» (se recuerda).
import { useCallback, useEffect, useState } from 'react';
import { GitMerge, UserX, Loader2, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { tr } from '@/contexts/LanguageContext';

const Side = ({ c }) => (
  <div className="min-w-0 flex-1">
    <p className="text-sm font-semibold truncate">{c.name}</p>
    <p className="text-[11px] text-muted-foreground truncate">{[c.phone, ...(c.alt_phones || [])].filter(Boolean).join(', ') || tr("sin teléfono")}</p>
    <p className="text-[11px] text-muted-foreground truncate">{[c.email, ...(c.alt_emails || [])].filter(Boolean).join(', ') || tr("sin correo")}</p>
    <p className="text-[10px] text-muted-foreground">{c.bookings_count} {tr("citas")}{c.has_account ? ` · ${tr("con cuenta")}` : ''}</p>
  </div>
);

export const NameDuplicatesCard = ({ allBarbers = false, barberId = null, onChanged, compact = false }) => {
  const [data, setData] = useState(null);
  const [busyPair, setBusyPair] = useState(null);
  const qs = allBarbers ? '?all_barbers=true' : (barberId ? `?barber_id=${barberId}` : '');

  const load = useCallback(() => api.get(`/clients/name-duplicates${qs}`).then(setData).catch(() => setData({ pairs: [], total: 0 })), [qs]);
  useEffect(() => { load(); }, [load]);

  const act = async (p, kind) => {
    setBusyPair(p.pair);
    try {
      const body = { a_id: p.a.id, b_id: p.b.id, barber_id: p.barber_id || undefined };
      if (kind === 'merge') { await api.post('/clients/name-duplicates/merge', body); toast.success(tr("Fichas fusionadas en una sola")); }
      else { await api.post('/clients/name-duplicates/distinct', body); toast.success(tr("Anotado: son personas distintas")); }
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.payload?.detail || tr("No se pudo completar")); } finally { setBusyPair(null); }
  };

  if (!data) return null;
  if (!data.total) {
    return compact ? null : <p className="text-[11px] text-muted-foreground" data-testid="name-dups-empty">{tr("Sin posibles duplicados por nombre.")}</p>;
  }
  return (
    <section className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="name-dups-card">
      <p className="text-sm font-semibold flex items-center gap-1.5"><Users className="w-4 h-4 text-amber-500" />{tr("Posibles duplicados por nombre")} <span className="text-xs text-muted-foreground font-normal" data-testid="name-dups-total">({data.total})</span></p>
      <p className="text-[11px] text-muted-foreground">{tr("Mismo nombre con teléfono o correo distintos. Decide tú: al fusionar se conservan los dos contactos en una sola ficha.")}</p>
      {data.pairs.map((p) => (
        <div key={p.pair} className="rounded-xl border border-border bg-card p-2.5 space-y-2" data-testid={`name-dup-pair-${p.pair}`}>
          {allBarbers && <p className="text-[10px] uppercase tracking-wide text-primary">{p.barber}</p>}
          <div className="flex gap-2"><Side c={p.a} /><span className="text-muted-foreground self-center text-xs">⇄</span><Side c={p.b} /></div>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 rounded-full gap-1" disabled={busyPair === p.pair} onClick={() => act(p, 'merge')} data-testid={`name-dup-merge-${p.pair}`}>
              {busyPair === p.pair ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitMerge className="w-3.5 h-3.5" />}{tr("Fusionar")}
            </Button>
            <Button size="sm" variant="outline" className="flex-1 rounded-full gap-1" disabled={busyPair === p.pair} onClick={() => act(p, 'distinct')} data-testid={`name-dup-distinct-${p.pair}`}>
              <UserX className="w-3.5 h-3.5" />{tr("Son personas distintas")}
            </Button>
          </div>
        </div>
      ))}
    </section>
  );
};

export default NameDuplicatesCard;
